import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance2";
import { API_BASE_URL } from "../config/apiConfig";

import {
  Card, Tabs, Typography, Upload, Button, Space, Row, Col, Form, Input,
  Divider, message, Avatar, Skeleton, Alert, List, Popconfirm, Switch, Tag,
} from "antd";
import { UploadOutlined, DeleteOutlined, PaperClipOutlined, InfoCircleOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const KNOWN = [
  { name: "BookingConfirmation", label: "Booking Confirmation" },
  { name: "CheckIn", label: "Check-In" },
  { name: "CheckOut", label: "Check-Out" },
];

// tolerant getters (Pascal/camel)
const val = (o, pascal, camel, fallback = undefined) => o?.[pascal] ?? o?.[camel] ?? fallback;
const getName = (t) => val(t, "TemplateName", "templateName", "");
const getId = (t) => val(t, "EmailTemplateId", "emailTemplateId", null);
const getSubject = (t) => val(t, "Subject", "subject", "");
const getHtml = (t) => val(t, "HtmlContent", "htmlContent", "");
const getDesc = (t) => val(t, "Description", "description", "");
const getHasAtt = (t) => val(t, "HasAttachment", "hasAttachment", false);
const getPattern = (t) => val(t, "AttachmentNamePattern", "attachmentNamePattern", "");
const getAttachments = (t) => val(t, "Attachments", "attachments", []) || [];

const getAttId = (a) => val(a, "EmailTemplateAttachmentId", "emailTemplateAttachmentId", null);
const getAttName = (a) => val(a, "FileName", "fileName", "");
const getAttPath = (a) => val(a, "FilePath", "filePath", "");
const getAttType = (a) => val(a, "ContentType", "contentType", "file");

// normalize relative -> absolute
const toAbs = (u) => {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  const base = (API_BASE_URL || "").replace(/\/+$/, "");
  return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
};

const HotelSettings = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [logoUrl, setLogoUrl] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [form] = Form.useForm();

  const seedFormFrom = (items) => {
    const init = {};
    (items || []).forEach((t) => {
      const name = getName(t);
      if (!name) return;
      init[`${name}_Subject`] = getSubject(t);
      init[`${name}_HtmlContent`] = getHtml(t);
      init[`${name}_Description`] = getDesc(t);
      init[`${name}_HasAttachment`] = !!getHasAtt(t);
      init[`${name}_AttachmentNamePattern`] = getPattern(t) ?? "";
    });
    KNOWN.forEach(({ name }) => {
      init[`${name}_Subject`] ??= "";
      init[`${name}_HtmlContent`] ??= "";
      init[`${name}_Description`] ??= "";
      init[`${name}_HasAttachment`] ??= false;
      init[`${name}_AttachmentNamePattern`] ??= "";
    });
    form.setFieldsValue(init);
  };

  useEffect(() => {
    if (!hotelId) return;

    const load = async () => {
      try {
        setLoading(true);

        // fetch persisted logo (see backend GET /Hotel/{id}/logo below)
        try {
          const r = await axiosInstance.get(`/Hotel/${hotelId}/logo`);
          const raw = r?.data?.logoUrl ?? r?.data?.LogoUrl;
          if (raw) setLogoUrl(toAbs(raw));
        } catch {
          // optional fallback to GET /Hotel/{id} if you store LogoUrl on the hotel row
          try {
            const r2 = await axiosInstance.get(`/Hotel/${hotelId}`);
            const raw2 = r2?.data?.logoUrl ?? r2?.data?.LogoUrl ?? r2?.data?.logoURL;
            if (raw2) setLogoUrl(toAbs(raw2));
          } catch {}
        }

        const { data } = await axiosInstance.get(`/EmailTemplates/${hotelId}`);
        const list = Array.isArray(data) ? data : [];
        setTemplates(list);
        seedFormFrom(list);
      } catch (err) {
        message.error(err?.response?.data?.message || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const byName = useMemo(() => {
    const m = new Map();
    for (const t of templates) m.set(getName(t), t);
    return m;
  }, [templates]);

  // POST /Hotel/{id}/hotellogo
  const handleLogoUpload = async ({ file }) => {
    if (!hotelId) return;
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // let Axios set the multipart boundary — no manual Content-Type
      const { data } = await axiosInstance.post(`/Hotel/${hotelId}/hotellogo`, fd);
      const raw = data?.logoUrl ?? data?.LogoUrl;
      setLogoUrl(raw ? `${toAbs(raw)}?v=${Date.now()}` : null); // cache-bust
      message.success("Logo updated");
    } catch (err) {
      message.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setFileUploading(false);
    }
  };

  const onSaveTemplates = async () => {
    try {
      const values = await form.validateFields();
      const payload = KNOWN.map((k) => ({
        HotelId: hotelId,
        TemplateName: k.name,
        Subject: values[`${k.name}_Subject`],
        HtmlContent: values[`${k.name}_HtmlContent`],
        Description: values[`${k.name}_Description`] || "",
        HasAttachment: !!values[`${k.name}_HasAttachment`],
        AttachmentNamePattern: values[`${k.name}_AttachmentNamePattern`] || "",
      }));

      setSaving(true);
      await axiosInstance.put(`/EmailTemplates/${hotelId}`, payload);
      message.success("Templates saved");

      const { data: fresh } = await axiosInstance.get(`/EmailTemplates/${hotelId}`);
      const list = Array.isArray(fresh) ? fresh : [];
      setTemplates(list);
      seedFormFrom(list);
    } catch (err) {
      if (!err?.errorFields) {
        message.error(err?.response?.data?.message || "Failed to save templates");
      }
    } finally {
      setSaving(false);
    }
  };

  const uploadAttachments = async (templateName, files) => {
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await axiosInstance.post(
        `/EmailTemplates/${hotelId}/${templateName}/attachments`,
        fd
      );
      const newAtts = Array.isArray(data) ? data : [data];
      setTemplates((prev) =>
        prev.map((t) =>
          getName(t) !== templateName
            ? t
            : { ...t, Attachments: [...getAttachments(t), ...newAtts] }
        )
      );
      message.success("Attachment(s) uploaded");
    } catch (err) {
      message.error(err?.response?.data?.message || "Attachment upload failed");
    }
  };

  const deleteAttachment = async (t, att) => {
    try {
      const templateId = getId(t);
      const attId = getAttId(att);
      if (templateId == null || attId == null) {
        message.error("Missing attachment identifiers.");
        return;
      }
      await axiosInstance.delete(`/EmailTemplates/${hotelId}/${templateId}/attachments/${attId}`);
      setTemplates((prev) =>
        prev.map((x) =>
          getId(x) !== templateId
            ? x
            : { ...x, Attachments: getAttachments(x).filter((a) => getAttId(a) !== attId) }
        )
      );
      message.success("Attachment deleted");
    } catch (err) {
      message.error(err?.response?.data?.message || "Delete failed");
    }
  };

  const emailHelp = (
    <Alert
      type="info"
      showIcon
      message="Placeholders"
      description={
        <div>
          Use <Text code>{"{{TokenName}}"}</Text> in subject/body. Common tokens:
          <div style={{ marginTop: 8 }}>
            <Text code>HotelName</Text>, <Text code>BookingNo</Text>, <Text code>GuestFirstName</Text>,{" "}
            <Text code>GuestLastName</Text>, <Text code>CheckInDate</Text>, <Text code>CheckOutDate</Text>,{" "}
            <Text code>RoomNumber</Text>, <Text code>Currency</Text>, <Text code>TotalAmount</Text>,{" "}
            <Text code>PaidAmount</Text>, <Text code>Balance</Text>
          </div>
        </div>
      }
      style={{ marginBottom: 16 }}
    />
  );

  if (!hotelId) {
    return <Alert type="warning" showIcon message="Please log in" description="Hotel ID not found." />;
  }

  return (
    <div style={{ margin: "0 auto" }}>
      <Card
        title={
          <Space>
            <Title level={4} style={{ marginBottom: 0 }}>Hotel Settings</Title>
            <Button type="text" icon={<InfoCircleOutlined />} onClick={() => message.info("Upload your logo and customize email templates for your property.")} />
          </Space>
        }
      >
        {loading ? (
          <Skeleton active />
        ) : (
          <Tabs defaultActiveKey="branding">
            {/* BRANDING */}
            <TabPane tab="Branding" key="branding">
              <Row gutter={[24, 24]} align="top">
                <Col xs={24} sm={10} md={8}>
                  <Card size="small" title="Current Logo" bodyStyle={{ textAlign: "center" }}>
                    {logoUrl ? (
                      <Avatar
                        shape="square"
                        src={logoUrl}
                        alt="Hotel Logo"
                        style={{ width: 160, height: 160, margin: "8px auto", borderRadius: 16 }}
                      />
                    ) : (
                      <div style={{ padding: 24 }}>
                        <Text type="secondary">No logo uploaded</Text>
                      </div>
                    )}
                  </Card>
                </Col>
                <Col xs={24} sm={14} md={16}>
                  <Card size="small" title="Upload New Logo">
                    <Upload.Dragger
                      multiple={false}
                      accept=".png,.jpg,.jpeg,.gif,.webp,.svg"
                      customRequest={({ file, onSuccess, onError }) => {
                        handleLogoUpload({ file }).then(() => onSuccess?.("ok")).catch(onError);
                      }}
                      showUploadList={false}
                      disabled={fileUploading}
                    >
                      <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                      <p className="ant-upload-text">Click or drag file to this area to upload</p>
                      <p className="ant-upload-hint">PNG/JPG/GIF/SVG, up to 10MB</p>
                    </Upload.Dragger>
                    <div style={{ marginTop: 12 }}>
                      <Button loading={fileUploading} disabled block>
                        {fileUploading ? "Uploading..." : "Upload"}
                      </Button>
                    </div>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            {/* EMAILS */}
            <TabPane tab="Emails" key="emails">
              {emailHelp}
              <Form layout="vertical" form={form}>
                {KNOWN.map(({ name, label }) => {
                  const t = byName.get(name);
                  const atts = getAttachments(t);
                  return (
                    <Card key={name} title={label} style={{ marginBottom: 16 }} size="small">
                      <Row gutter={16}>
                        <Col xs={24} md={10}>
                          <Form.Item
                            label="Subject"
                            name={`${name}_Subject`}
                            rules={[{ required: true, message: "Subject is required" }, { max: 200, message: "Max 200 characters" }]}
                          >
                            <Input placeholder="Subject..." />
                          </Form.Item>

                          <Form.Item label="Description" name={`${name}_Description`}>
                            <Input placeholder="Optional short description" />
                          </Form.Item>

                          <Form.Item label="Include attachment?" name={`${name}_HasAttachment`} valuePropName="checked">
                            <Switch />
                          </Form.Item>

                          <Form.Item label="Attachment file name pattern" name={`${name}_AttachmentNamePattern`}>
                            <Input placeholder="e.g., Invoice_{{BookingNo}}.pdf" />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={14}>
                          <Form.Item
                            label="Body (HTML supported)"
                            name={`${name}_HtmlContent`}
                            rules={[{ required: true, message: "Body is required" }]}
                          >
                            <TextArea rows={8} placeholder="<p>Hello {{GuestFirstName}}...</p>" />
                          </Form.Item>

                          <Divider orientation="left">Attachments</Divider>
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <Upload
                              multiple
                              beforeUpload={() => false}
                              showUploadList={false}
                              accept=".pdf,.png,.jpg,.jpeg,.webp"
                              onChange={(info) => {
                                const files = info.fileList.map((f) => f.originFileObj).filter(Boolean);
                                if (files.length) {
                                  uploadAttachments(name, files);
                                  info.fileList.length = 0;
                                }
                              }}
                            >
                              <Button icon={<UploadOutlined />}>Upload Attachments</Button>
                            </Upload>

                            <List
                              size="small"
                              dataSource={atts}
                              locale={{ emptyText: "No attachments" }}
                              renderItem={(att) => {
                                const href = toAbs(getAttPath(att));
                                return (
                                  <List.Item
                                    actions={[
                                      <Popconfirm key="del" title="Delete attachment?" onConfirm={() => deleteAttachment(t, att)}>
                                        <Button danger type="text" icon={<DeleteOutlined />} />
                                      </Popconfirm>,
                                      href ? (
                                        <a key="dl" href={href} target="_blank" rel="noreferrer">Download</a>
                                      ) : null,
                                    ]}
                                  >
                                    <List.Item.Meta
                                      avatar={<PaperClipOutlined />}
                                      title={getAttName(att)}
                                      description={
                                        <Space>
                                          <Tag>{getAttType(att) || "file"}</Tag>
                                          <Text type="secondary">{href || getAttPath(att)}</Text>
                                        </Space>
                                      }
                                    />
                                  </List.Item>
                                );
                              }}
                            />
                          </Space>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}

                <Space>
                  <Button type="primary" loading={saving} onClick={onSaveTemplates}>Save Templates</Button>
                  <Button onClick={() => { seedFormFrom(templates); message.info("Reverted unsaved changes."); }}>
                    Revert
                  </Button>
                </Space>
              </Form>
            </TabPane>
          </Tabs>
        )}
      </Card>
    </div>
  );
};

export default HotelSettings;
